import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { LeftOutlined, RightOutlined, WarningFilled } from "@ant-design/icons";
import { AppContext } from "../../contexts/AppContext";
import SubHeader from "../../components/SubHeader/SubHeader";
import Dialog from "../../components/Dialog/Dialog";
import GeneralButton from "../../components/GeneralButton/GeneralButton";
import { showToast } from "../../utils/commonUtils";
import {
  getReservationCalendar,
  changeReservationDateTime,
} from "../../services/service";
import {
  RESERVATION_TYPES,
  DATETIME_SELECT_MODE,
  WEEKDAY_TYPES,
} from "../../config/setting";
import { createDateMessage, getConstValue } from "../../utils/commonUtils";
import dayjs from "dayjs";
import "../../styles/globals.css";
import "./DatetimeSelectPage.css";

/**
 * 日時選択画面用のコンポーネント
 * @param {} なし
 * @return {} なし
 */
function DatetimeSelectPage() {
  const {
    globalLoading,
    globalMedicalInstitutionId,
    globalMedicalInstitutionName,
    globalSelectMenu,
    globalDatetimeSelectMode,
    globalReservationToken,
    globalReservationDetail,
    globalSelectReservationDateTime,
    setGlobalSelectReservationInfo,
    setGlobalSelectReservationDateTime,
    globalLoginUserInfo,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isNavDialogOpen, setNavDialogOpen] = useState(false);
  const [dataCalnder, setDataCalnder] = useState(null); // 予約カレンダーのデータ
  const [selectInfoDisp, setSelectInfoDisp] = useState(null); // 表示用選択情報
  const [dispTitle, setDispTitle] = useState("");
  const [currentDateRange, setCurrentDateRange] = useState(
    new Date(new Date().setHours(0, 0, 0, 0))
  ); // 現在の日付範囲

  const [errorMessage, setErrorMessage] = useState(null);

  /**
   * ブラウザバック時のダイアログ確認
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setNavDialogOpen(true); // ここで確認ダイアログを開くように変更
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // リロード時のナビゲーション確認
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      return (event.returnValue = "ナビゲーションの確認");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // 表示開始日付が更新された時
  useEffect(() => {
    const loadDatas = async () => {
      try {
        const requestDate = dayjs(currentDateRange).format("YYYY-MM-DD");
        let reservationMenuId = null;
        if (globalDatetimeSelectMode === DATETIME_SELECT_MODE.CHANGE) {
          reservationMenuId = globalReservationDetail?.reservationMenuId;
        } else {
          reservationMenuId = globalSelectMenu?.reservationMenuId;
        }

        if (globalMedicalInstitutionId && reservationMenuId) {
          const reservationCalender = await getReservationCalendar(
            globalMedicalInstitutionId,
            reservationMenuId,
            requestDate
          );
          setDataCalnder(reservationCalender?.data);

          // タイトルセット
          const minDateString =
            dayjs(currentDateRange).format("YYYY年MM月DD日");
          const maxDateString = dayjs(currentDateRange)
            .add(6, "day")
            .format("YYYY年MM月DD日");

          const title = `${minDateString} - ${maxDateString}`;

          setDispTitle(title);
        }
      } catch (error) {
        console.error("Error load data:", error); // ここでエラーをキャッチしてログ出力
      }
    };

    loadDatas();
  }, [
    currentDateRange,
    globalLoading,
    globalDatetimeSelectMode,
    globalMedicalInstitutionId,
    globalReservationDetail,
    globalSelectMenu,
  ]);

  /**
   * 戻るボタンクリック時
   * @param {} なし
   * @return {} なし
   */
  const handleBackClick = () => {
    setNavDialogOpen(true);
  };

  // ダイアログを開く
  const openDialog = () => setDialogOpen(true);

  // ダイアログを閉じる
  const closeDialog = () => setDialogOpen(false);

  /**
   * セルがクリックされたときの処理
   * @param {string} day 日別予約枠
   * @param {string} index 選択時間INDEX
   */
  const handleCellClick = (day, index) => {
    const reservationType = day.timeList[index].reservationType;

    // 選択中の日付かどうか
    const isSelectDate =
      day?.date === globalReservationDetail?.reservationDate &&
      day?.timeList[index]?.time === globalReservationDetail?.reservationTime;

    if (
      (reservationType === RESERVATION_TYPES.AVAILABLE.id ||
        reservationType === RESERVATION_TYPES.FEW_LEFT.id) &&
      !isSelectDate
    ) {
      const dispDate = createDateMessage(day.date);
      const dispTime = day?.timeList[index]?.time;

      setGlobalSelectReservationInfo(day);
      setGlobalSelectReservationDateTime({
        date: day.date,
        time: day?.timeList[index]?.time,
        timeSlotId: day?.timeList[index]?.timeSlotId,
        isTemporaryTimeSlot: day?.timeList[index]?.isTemporaryTimeSlot,
      });
      setSelectInfoDisp({ dispDate, dispTime });

      openDialog();
    }
  };

  /**
   * ダイアログのボタンがクリックされたときの処理
   * @param {number} buttonIndex - クリックされたボタンのインデックス
   */
  const handleButtonClick = async (buttonIndex) => {
    setDialogOpen(false);

    if (buttonIndex === 1) {
      if (globalDatetimeSelectMode === DATETIME_SELECT_MODE.CHANGE) {
        // 予約変更
        try {
          // リクエスト用に加工
          const data = await changeReservationDateTime(
            globalReservationToken,
            globalSelectReservationDateTime?.date,
            globalSelectReservationDateTime?.time.split("-")[0].trim()
          );

          if (data.result.status === "OK") {
            showToast("予約日時を変更しました", "success");

            const newReservationToken = data?.data?.reservationToken;

            // 予約詳細画面に遷移
            navigate(
              `/reservation-information?token=${newReservationToken}#medicalInstitutionId=${globalMedicalInstitutionId}`
            );
          } else {
            // エラーメッセージセット
            setErrorMessage(data?.result?.error_message);
          }
        } catch (error) {
          console.error("Error changeReservationDateTime:", error);
          showToast("予約変更に失敗しました", "error");
        }
      } else {
        if (!globalLoginUserInfo) {
          // ログイン前の場合
          navigate("/account-verify");
        } else {
          // ログイン後の場合
          navigate("/patient-select");
        }
      }
    }
  };

  /**
   * ナビゲーションの確認ダイアログのボタンがクリックされたときの処理
   * @param {number} buttonIndex - クリックされたボタンのインデックス
   */
  const handleNavDialogClose = (confirmNavigation) => {
    setNavDialogOpen(false);
    if (confirmNavigation) {
      if (globalDatetimeSelectMode === DATETIME_SELECT_MODE.CHANGE) {
        // 予約情報確認画面に遷移
        navigate(
          `/reservation-information?token=${globalReservationToken}#medicalInstitutionId=${globalMedicalInstitutionId}`
        );
      } else {
        // 初診・再診選択画面に遷移
        navigate("/appointment-select");
      }
    }
  };

  const normalButtons = [
    {
      label: "選び直す",
      width: "80px",
      type: "secondary-white",
      onClick: handleButtonClick,
    },
    {
      label: "進む",
      width: "80px",
      type: "primary-blue",
      onClick: handleButtonClick,
    },
  ];

  const changeButtons = [
    {
      label: "選び直す",
      width: "80px",
      type: "secondary-white",
      onClick: handleButtonClick,
    },
    {
      label: "変更確定",
      width: "80px",
      type: "primary-blue",
      onClick: handleButtonClick,
    },
  ];

  const navButtons = [
    {
      label: "このページから離れる",
      width: "140px",
      type: "primary-blue",
      onClick: () => handleNavDialogClose(true),
    },
    {
      label: "このページにとどまる",
      width: "140px",
      type: "secondary-white",
      onClick: () => handleNavDialogClose(false),
    },
  ];

  // 左ボタンのクリック処理
  const handleLeftClick = () => {
    const newDate = new Date(currentDateRange);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDateRange(newDate);
  };

  // 右ボタンのクリック処理
  const handleRightClick = () => {
    const newDate = new Date(currentDateRange);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDateRange(newDate);
  };

  /**
   * クラス名を生成する関数
   * @param {string} day 日別予約枠
   * @param {string} index 選択時間INDEX
   */
  const getClassName = (day, index) => {
    const reservationTypeClass = getConstValue(
      RESERVATION_TYPES,
      day.timeList[index].reservationType,
      "className"
    );

    // 選択中の日付かどうか
    const isSelectDate =
      day?.date === globalReservationDetail?.reservationDate &&
      day?.timeList[index]?.time === globalReservationDetail?.reservationTime;

    const selectDateClass = isSelectDate ? "select-date" : "";

    return `cell ${reservationTypeClass} ${selectDateClass}`;
  };

  return (
    <div className="datetime-select">
      <SubHeader clinicName={globalMedicalInstitutionName} />
      <div className="main-area">
        {!globalDatetimeSelectMode ? (
          <div className="title">日時を選択</div>
        ) : (
          <div className="title">日時を変更</div>
        )}
        {errorMessage && (
          <div className="error-area">
            <div className="icon-area">
              <WarningFilled className="icon" />
            </div>
            <label>{errorMessage}</label>
          </div>
        )}
        <div className="reserve-area">
          <div className="reserve-title-area">
            <Button
              className="reserve-button"
              onClick={handleLeftClick}
              disabled={
                dataCalnder && new Date(dataCalnder[0].date) <= new Date()
              }
            >
              <LeftOutlined />
            </Button>
            <div className="reserve-title">{dispTitle}</div>
            <Button className="reserve-button" onClick={handleRightClick}>
              <RightOutlined />
            </Button>
          </div>
          <div className="reserve-calendar-area">
            {dataCalnder && dataCalnder != null ? (
              <table>
                <thead>
                  <tr>
                    <td className="col-title"></td>
                    {dataCalnder.map((day) => (
                      <td
                        key={day.date}
                        className={`col-title ${getConstValue(
                          WEEKDAY_TYPES,
                          new Date(day.date).getDay(),
                          "className"
                        )}`}
                      >
                        <div>{new Date(day.date).getDate()}</div>
                        <div>
                          (
                          {getConstValue(
                            WEEKDAY_TYPES,
                            new Date(day.date).getDay(),
                            "abbr"
                          )}
                          )
                        </div>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataCalnder[0].timeList.map((timeSlot, index) => (
                    <tr key={index}>
                      <td className="row-title">
                        {timeSlot.time.split("-")[0]}
                      </td>
                      {dataCalnder.map((day) => (
                        <td
                          className={getClassName(day, index)}
                          key={day.date + timeSlot.time}
                          data-testid={`cell-${day.date}-${timeSlot.time}`}
                          onClick={() =>
                            handleCellClick(day, index, timeSlot.time)
                          }
                        >
                          {getConstValue(
                            RESERVATION_TYPES,
                            day.timeList[index].reservationType,
                            "mark"
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-reservation-menu-slot">
                <p>
                  この期間には診療時間が設定されていません。
                  <br />
                  他の日付を選択してください。
                </p>
              </div>
            )}
          </div>
        </div>
        <Dialog
          title={!globalDatetimeSelectMode ? "日時の確認" : "日時変更の確認"}
          isOpen={isDialogOpen}
          onClose={closeDialog}
          buttons={!globalDatetimeSelectMode ? normalButtons : changeButtons}
        >
          <div className="dialog-bold-area">
            {selectInfoDisp?.dispDate}
            <br />
            {selectInfoDisp?.dispTime}
          </div>
          <div className="dialog-normal-area">
            {!globalDatetimeSelectMode
              ? "以上の日時でよろしいですか？"
              : "以上の日時に変更でよろしいですか？"}
          </div>
        </Dialog>
        <Dialog
          title="ナビゲーションの確認"
          isOpen={isNavDialogOpen}
          onClose={() => setNavDialogOpen(false)}
          buttons={navButtons}
        >
          <div className="dialog-normal-area">
            {"本当に前のページに遷移しますか？"}
          </div>
        </Dialog>
        <GeneralButton
          buttonLabel={
            !globalDatetimeSelectMode
              ? "初再診選択に戻る"
              : "予約内容の確認に戻る"
          }
          type={"secondary-white"}
          onClick={() => handleBackClick()}
        />
      </div>
    </div>
  );
}

export default DatetimeSelectPage;
